/**
 * DiagContractQuality: Diagnostics drawer -- universe contract quality (tenor-driven grade), moved verbatim out of src/pages/ScreenView.tsx (Screener Phase 3, 2026-10-28). Display only.
 */
import React from "react";
import { MICRO_LABEL } from "@components/cockpit";
import { badgeStyle, mutedText } from "@components/screener/screenerUi";
import { TENOR_DRIVEN_GRADE_TOOLTIP, gradeWithTenorNote, numberFmt, pctFmt } from "@lib/screenerTruth";
import { toneFromEntryGrade } from "@lib/screenerViewHelpers";
import { type UniverseGrade } from "@lib/screenerTypes";

export type DiagContractQualityProps = {
  candidateUniverseSummary: { total: number; selected: number; ranked: number; skipped: number; };
  contractQualityGrade: UniverseGrade;
};

export default function DiagContractQuality({
  candidateUniverseSummary,
  contractQualityGrade,
}: DiagContractQualityProps) {
  return (
    <div>
      <div className={`${MICRO_LABEL} mb-2`} style={mutedText}>Universe contract quality</div>
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
        <div>
          <div className="text-xs" style={mutedText}>Contract Quality</div>
          <div className="mt-1 flex items-center gap-2 font-semibold" title={TENOR_DRIVEN_GRADE_TOOLTIP}>
            <span style={badgeStyle(toneFromEntryGrade(contractQualityGrade.grade))} data-testid="screener-contract-quality">
              {gradeWithTenorNote(contractQualityGrade.grade)}
            </span>
            <span>{numberFmt(contractQualityGrade.score, 3)}</span>
          </div>
          {contractQualityGrade.label ? <div className="text-[11px]" style={mutedText}>{contractQualityGrade.label}</div> : null}
        </div>
        <div>
          <div className="text-xs" style={mutedText}>Ranked Contracts</div>
          <div className="mt-1 font-semibold">{candidateUniverseSummary.ranked || candidateUniverseSummary.total}</div>
        </div>
        <div>
          <div className="text-xs" style={mutedText}>Avg / Top-5 Entry</div>
          <div className="mt-1 font-semibold">{numberFmt(contractQualityGrade.components?.avgEntryScore, 3)} / {numberFmt(contractQualityGrade.components?.topEntryScore, 3)}</div>
        </div>
        <div>
          <div className="text-xs" style={mutedText}>Avg Surface</div>
          <div className="mt-1 font-semibold">{numberFmt(contractQualityGrade.components?.avgSurfaceScore, 3)}</div>
        </div>
        <div>
          <div className="text-xs" style={mutedText}>A/B · C+ Breadth</div>
          <div className="mt-1 font-semibold">{pctFmt(contractQualityGrade.components?.abShare, 0)} · {pctFmt(contractQualityGrade.components?.cOrBetterShare, 0)}</div>
        </div>
      </div>
      <div className="mt-2 text-[11px]" style={mutedText}>{TENOR_DRIVEN_GRADE_TOOLTIP}</div>
    </div>
  );
}
